import { useEffect, useId, useRef } from 'react'
import { CowboyHat } from '@/components/emma/cowboyHat'
import { EmmaEngine, EMMA_COLORS, type EmmaSkin } from '@/components/emma/emmaEngine'
import { useProfile } from '@/hooks/useProfile'

/**
 * A small Emma for a note on a screen (settings-topics.html #016: Emma stays on
 * screen, walking you through each pill). Her own rig, so she never takes over
 * the tab bar's or the sidebar's Emma (the chat drives that one). `talk` bumps
 * her into answering for a moment, as Brian's guide does on each step.
 */
export function EmmaMini({ size = 40, talk }: { size?: number; talk?: unknown }) {
  const uid = 'emmamini' + useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const svgRef = useRef<SVGSVGElement>(null)
  const engineRef = useRef<EmmaEngine | null>(null)
  const { data: profile } = useProfile()
  const fill = EMMA_COLORS.find((c) => c.id === profile?.emma_color || c.hex === profile?.emma_color)?.hex ?? '#c8854a'
  const skin = (profile?.emma_skin as EmmaSkin | null) ?? 'seed'

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const $ = <T extends Element>(id: string) => svg.querySelector(`#${id}`) as T
    const reduced = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const engine = new EmmaEngine({
      svg,
      body: $(`${uid}Body`), over: $(`${uid}Over`), eyeL: $(`${uid}L`), eyeR: $(`${uid}R`),
      clip: $(`${uid}ClipPath`), ridge: $(`${uid}Ridge`), shade: $(`${uid}Shade`), hat: $(`${uid}Hat`),
    }, { skin, reducedMotion: reduced })
    engine.setRestAlert(true)
    engineRef.current = engine
    return () => { engine.destroy(); engineRef.current = null }
  }, [uid]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { engineRef.current?.setSkin(skin) }, [skin])
  useEffect(() => { if (talk !== undefined) engineRef.current?.ask() }, [talk])

  return (
    <span className="emma-mini" style={{ width: size, height: size }} aria-hidden="true">
      <svg ref={svgRef} viewBox="0 0 512 512" className="emma-svg">
        <path id={`${uid}Body`} fill={fill} d="" />
        <g id={`${uid}Over`} />
        <path id={`${uid}L`} fill="#000" d="" />
        <path id={`${uid}R`} fill="#000" d="" />
        <clipPath id={`${uid}Clip`}><path id={`${uid}ClipPath`} d="" /></clipPath>
        <g clipPath={`url(#${uid}Clip)`}>
          <path id={`${uid}Ridge`} fill={fill} d="" />
          <path id={`${uid}Shade`} fill="none" stroke="#000" strokeWidth={4} strokeLinecap="round" transform="translate(0,2)" d="" />
        </g>
        <CowboyHat id={`${uid}Hat`} />
      </svg>
    </span>
  )
}
